import { ReactNode, useEffect, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  User,
} from "firebase/auth";

import { auth } from "../lib/firebase";
import { AuthContext, AuthContextType } from "./AuthContext";
import { callAuthMe } from "./authMe";
import { env } from "../config/env";
import { StorageKeys } from "../constants";

const PENDING_REGISTRATION_KEY = StorageKeys.pendingRegistration;

/**
 * Uses sessionStorage instead of localStorage so that pending registration state
 * is automatically cleared when the browser tab/window is closed.
 * This provides better UX: users don't get stuck in registration mode after
 * closing the browser and returning later.
 */
const _safeSetPendingRegistration = (v: boolean) => {
  try {
    if (v) {
      window.sessionStorage.setItem(PENDING_REGISTRATION_KEY, "1");
    } else {
      window.sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
    }
  } catch {
    // ignore
  }
};

const _safeHasPendingRegistration = (): boolean => {
  try {
    return window.sessionStorage.getItem(PENDING_REGISTRATION_KEY) === "1";
  } catch {
    return false;
  }
};

const googleProvider = new GoogleAuthProvider();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // only for initial auth state
  const [authStatus, setAuthStatus] =
    useState<AuthContextType["authStatus"]>("unknown");
  const [authMe, setAuthMe] = useState<AuthContextType["authMe"]>(undefined);
  const verifyingRef = useRef(false);
  // Track if we've already verified this user to avoid duplicate auth/me calls
  const verifiedUidRef = useRef<string | null>(null);
  // Track current authStatus for closure-safe access in onAuthStateChanged
  const authStatusRef = useRef<AuthContextType["authStatus"]>("unknown");
  // Track which UID is currently being authorized (prevents race conditions in Strict Mode)
  const authorizingUidRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // Gate exposure of the user until authorization is confirmed.
      (async () => {
        if (!currentUser) {
          setUser(null);
          setAuthMe(undefined);
          setAuthStatus("unknown");
          setLoading(false);
          return;
        }

        // If we have a persisted "pending registration" marker,
        // start from a clean slate on reload.
        //
        // Important: we only clear the marker after a successful signOut.
        // If signOut fails for some reason, we keep the marker so the next reload
        // will retry the cleanup instead of calling /auth/me again.
        if (_safeHasPendingRegistration()) {
          try {
            await firebaseSignOut(auth);
            _safeSetPendingRegistration(false);
          } catch {
            // keep marker
          }

          setUser(null);
          setAuthMe(undefined);
          setAuthStatus("unknown");
          setLoading(false);
          return;
        }

        // If sign-in flow is already doing authorization, do nothing here.
        if (verifyingRef.current) return;

        // Skip if we've already verified this user (prevents duplicate calls on re-renders)
        if (
          verifiedUidRef.current === currentUser.uid &&
          authStatusRef.current === "allowed"
        ) {
          setLoading(false);
          return;
        }

        // Skip if we're already authorizing this user (prevents race conditions in Strict Mode)
        if (authorizingUidRef.current === currentUser.uid) {
          return;
        }

        // Mark that we're authorizing this user
        authorizingUidRef.current = currentUser.uid;

        try {
          await authorizeAndSetState(currentUser);
        } catch {
          await firebaseSignOut(auth);
          setUser(null);
          setAuthMe(undefined);
          setAuthStatus("unknown");
          return;
        } finally {
          setLoading(false);
        }
      })();
    });

    return () => unsubscribe();
  }, []);

  const authorizeAndSetState = async (u: User) => {
    const apiBaseUrl = env.apiBaseUrl;

    const token = await u.getIdToken();

    try {
      const me = await callAuthMe(apiBaseUrl, token);

      if (me.needsTokenRefresh) {
        const refreshed = await u.getIdToken(true);
        const me2 = await callAuthMe(apiBaseUrl, refreshed);
        if (me2.needsTokenRefresh) {
          // defensive: avoid infinite refresh loops
          throw new Error("Token refresh loop");
        }
        if (!me2.allowed) {
          setAuthStatus("forbidden");
          setAuthMe(me2);
          setUser(null);
          return;
        }
        verifiedUidRef.current = u.uid;
        authStatusRef.current = "allowed";
        setAuthStatus("allowed");
        setAuthMe(me2);
        setUser(u);
        return;
      }

      if (!me.allowed) {
        _safeSetPendingRegistration(false);
        setAuthStatus("forbidden");
        setAuthMe(me);
        setUser(null);
        return;
      }

      _safeSetPendingRegistration(false);
      verifiedUidRef.current = u.uid;
      authStatusRef.current = "allowed";
      setAuthStatus("allowed");
      setAuthMe(me);
      setUser(u);
    } catch (e) {
      const code =
        typeof e === "object" && e && "code" in e
          ? String((e as { code: unknown }).code)
          : "";
      if (code === "NOT_VERIFIED") {
        setAuthStatus("not_verified");
        setAuthMe(undefined);
        setUser(null);
        return;
      }
      if (code === "NEEDS_REGISTRATION") {
        // Mark so that a browser refresh resets the state (signs out) instead of
        // repeatedly re-triggering /auth/me and forcing register mode.
        _safeSetPendingRegistration(true);
        setAuthStatus("needs_registration");
        setAuthMe(undefined);
        setUser(null);
        return;
      }
      if (code === "FORBIDDEN") {
        setAuthStatus("forbidden");
        setAuthMe(undefined);
        setUser(null);
        return;
      }
      throw e;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    verifyingRef.current = true;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const u = cred.user;
      await authorizeAndSetState(u);
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    verifyingRef.current = true;
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await authorizeAndSetState(result.user);
    } catch (error) {
      await firebaseSignOut(auth);
      throw error;
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    // During signup we intentionally do NOT call `/auth/me`.
    // New users are expected to be blocked by the onboarding guard (NEEDS_REGISTRATION)
    // until they complete `/auth/register`.
    //
    // We also gate the onAuthStateChanged side-effect to avoid firing an extra `/auth/me`
    // request while the registration form is in progress.
    verifyingRef.current = true;
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      _safeSetPendingRegistration(true);
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const signOut = async () => {
    _safeSetPendingRegistration(false);
    verifiedUidRef.current = null;
    authStatusRef.current = "unknown";
    authorizingUidRef.current = null;
    await firebaseSignOut(auth);
    setUser(null);
    setAuthMe(undefined);
    setAuthStatus("unknown");
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!user) return null;
    return user.getIdToken();
  };

  const value: AuthContextType = {
    user,
    loading,
    authStatus,
    authMe,
    signInWithEmail,
    signInWithGoogle,
    signUpWithEmail,
    signOut,
    resetPassword,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
